import { Button, IconButton, Modal } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Unit } from '@warehouse/shared';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePermission } from '../../../auth/auth-store';
import { useConfirm } from '../../../components/ConfirmDialog';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { createUnit, listUnits, removeUnit, updateUnit } from '../api';
import { conflictDetails } from '../components/BarcodeConflict';
import { TextField } from '../components/form';
import { toastMessageError } from '../notify';

/**
 * The unit register. A unit carries no factor of its own — the multiplier is
 * per article (`article_units`), because "all sorts of values, set per item"
 * (Victor). Deleting one is refused while any article or conversion still
 * points at it, and the error says how many.
 */
export function UnitsPage(): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const { state, setState } = useListState({ defaultSort: 'code', defaultDir: 'asc' });
  const canEdit = usePermission('registers.articles.units');
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState<Unit | 'new' | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['master-data', 'units', listQueryKey(state)],
    queryFn: () => listUnits(state),
  });
  useLiveQuery(['articles'], ['master-data', 'units']);

  const remove = useMutation({
    mutationFn: (id: string) => removeUnit(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
      toastSuccess(t('masterData:units.removed'));
    },
    onError: (err: unknown) => {
      // "Konflikt — posten har ändrats" is true but useless here; the API says
      // exactly what is in the way, so say that instead.
      const conflict = conflictDetails(err);
      if (conflict?.reason === 'unit_in_use') {
        toastMessageError(
          t('masterData:units.inUse', {
            articles: conflict.params['articles'] ?? 0,
            conversions: conflict.params['conversions'] ?? 0,
          }),
        );
        return;
      }
      toastError(err);
    },
  });

  const columns: DataTableColumn<Unit>[] = [
    {
      id: 'code',
      header: t('masterData:units.columns.code'),
      sortField: 'code',
      width: 'w-32',
      cell: (row) => <span className="font-mono text-sm">{row.code}</span>,
    },
    { id: 'name', header: t('masterData:units.columns.name'), sortField: 'name', cell: (row) => row.name },
    {
      id: 'createdAt',
      header: t('masterData:units.columns.createdAt'),
      sortField: 'createdAt',
      hideBelow: 'md',
      cell: (row) => <span className="text-xs text-ink-muted">{formatDateTime(row.createdAt)}</span>,
    },
    {
      id: 'actions',
      header: '',
      width: 'w-24',
      align: 'right',
      cell: (row) =>
        canEdit ? (
          <div className="flex justify-end gap-1">
            <IconButton
              aria-label={t('common:actions.edit')}
              onClick={() => {
                setEditing(row);
              }}
            >
              <Pencil className="size-4" />
            </IconButton>
            <IconButton
              variant="danger"
              aria-label={t('common:actions.delete')}
              onClick={() => {
                void (async () => {
                  const ok = await confirm({
                    title: t('masterData:units.confirmRemove.title'),
                    message: t('masterData:units.confirmRemove.message', { code: row.code }),
                  });
                  if (ok) remove.mutate(row.id);
                })();
              }}
            >
              <Trash2 className="size-4" />
            </IconButton>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      {dialog}
      <PageHeader
        title={t('masterData:units.title')}
        subtitle={t('masterData:units.subtitle')}
        actions={
          canEdit ? (
            <Button
              icon={<Plus />}
              onClick={() => {
                setEditing('new');
              }}
            >
              {t('masterData:units.new')}
            </Button>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={data?.total ?? null}
        loading={isLoading}
        error={error}
        searchPlaceholder={t('masterData:units.searchPlaceholder')}
        emptyTitle={t('masterData:units.empty')}
        emptyDescription={t('masterData:units.emptyDescription')}
      />

      {editing ? (
        <UnitModal
          unit={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </>
  );
}

function UnitModal({ unit, onClose }: { unit: Unit | null; onClose: () => void }): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const queryClient = useQueryClient();
  const { register, handleSubmit, formState } = useForm<{ code: string; name: string }>({
    defaultValues: { code: unit?.code ?? '', name: unit?.name ?? '' },
  });

  const mutation = useMutation({
    mutationFn: (values: { code: string; name: string }) =>
      unit ? updateUnit(unit.id, values) : createUnit(values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
      toastSuccess(unit ? t('masterData:units.saved') : t('masterData:units.created'));
      onClose();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <Modal open onClose={onClose} title={unit ? t('masterData:units.edit') : t('masterData:units.new')} width={460}>
      <form
        onSubmit={(event) => {
          void handleSubmit((values) => {
            mutation.mutate(values);
          })(event);
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <TextField
          label={t('masterData:units.fields.code')}
          hint={t('masterData:units.hints.code')}
          autoFocus
          error={formState.errors.code ? t('common:common.required') : undefined}
          {...register('code', { required: true })}
        />
        <TextField
          label={t('masterData:units.fields.name')}
          error={formState.errors.name ? t('common:common.required') : undefined}
          {...register('name', { required: true })}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            {t('common:actions.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
